import { ViewportElementProvider } from '@blocksuite/affine-shared/services';
import type { BlockStdScope } from '@blocksuite/std';

/** Keep the overlay inside this editor pane, above its visible bottom dock. */
export function observeMaximapLayout(
  std: BlockStdScope,
  dialog: HTMLDialogElement
): () => void {
  const viewport =
    std.getOptional(ViewportElementProvider)?.viewportElement ?? std.host;
  const toolbar = std.store.root
    ? std.view.getWidget('edgeless-toolbar-widget', std.store.root.id)
    : null;
  const dock = toolbar?.shadowRoot?.querySelector<HTMLElement>(
    '.edgeless-toolbar-container'
  );
  const doc = dialog.ownerDocument;
  const win = doc.defaultView!;
  // Floating sidebars overlap the editor instead of reducing its layout width.
  // Reuse ResizePanel's existing edge/open contract for that responsive mode.
  const sidebars = Array.from(
    doc.querySelectorAll<HTMLElement>(
      '[data-is-floating][data-handle-position]'
    )
  );
  let frame: number | null = null;

  const update = () => {
    const pane = viewport.getBoundingClientRect();
    const editor = std.host.getBoundingClientRect();
    const visual = win.visualViewport;
    const screenLeft = visual?.offsetLeft ?? 0;
    const screenTop = visual?.offsetTop ?? 0;
    let left = Math.max(screenLeft, pane.left, editor.left);
    const top = Math.max(screenTop, pane.top, editor.top);
    let right = Math.min(
      screenLeft + (visual?.width ?? win.innerWidth),
      pane.right,
      editor.right
    );
    let bottom = Math.min(
      screenTop + (visual?.height ?? win.innerHeight),
      pane.bottom,
      editor.bottom
    );
    for (const sidebar of sidebars) {
      if (
        sidebar.dataset.open !== 'true' ||
        sidebar.dataset.isFloating !== 'true' ||
        win.getComputedStyle(sidebar).visibility === 'hidden'
      ) {
        continue;
      }
      const rect = sidebar.getBoundingClientRect();
      if (
        rect.width <= 0 ||
        rect.height <= 0 ||
        rect.bottom <= top ||
        rect.top >= bottom ||
        rect.right <= left ||
        rect.left >= right
      ) {
        continue;
      }
      if (sidebar.dataset.handlePosition === 'right') {
        left = Math.max(left, rect.right);
      } else if (sidebar.dataset.handlePosition === 'left') {
        right = Math.min(right, rect.left);
      }
    }
    if (toolbar && dock && win.getComputedStyle(toolbar).opacity !== '0') {
      const rect = dock.getBoundingClientRect();
      if (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.right > left &&
        rect.left < right &&
        rect.bottom > top &&
        rect.top < bottom
      ) {
        bottom = Math.max(top, rect.top - 12);
      }
    }

    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);
    Object.assign(dialog.style, {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    });
    dialog.style.setProperty(
      '--maximap-size',
      `${Math.max(0, Math.min(width, height) - 48)}px`
    );
  };
  const schedule = () => {
    if (frame !== null) return;
    frame = win.requestAnimationFrame(() => {
      frame = null;
      update();
    });
  };
  const observer = new ResizeObserver(schedule);
  for (const element of [viewport, std.host, toolbar, dock, ...sidebars]) {
    if (element) observer.observe(element);
  }
  const attributes = new MutationObserver(schedule);
  for (const element of sidebars) {
    attributes.observe(element, {
      attributes: true,
      attributeFilter: [
        'data-open',
        'data-is-floating',
        'data-transition-state',
      ],
    });
    element.addEventListener('transitionend', schedule);
  }
  win.addEventListener('resize', schedule);
  win.visualViewport?.addEventListener('resize', schedule);
  win.visualViewport?.addEventListener('scroll', schedule);
  doc.addEventListener('scroll', schedule, true);
  toolbar?.addEventListener('transitionend', schedule);
  update();

  return () => {
    observer.disconnect();
    attributes.disconnect();
    for (const element of sidebars) {
      element.removeEventListener('transitionend', schedule);
    }
    if (frame !== null) win.cancelAnimationFrame(frame);
    win.removeEventListener('resize', schedule);
    win.visualViewport?.removeEventListener('resize', schedule);
    win.visualViewport?.removeEventListener('scroll', schedule);
    doc.removeEventListener('scroll', schedule, true);
    toolbar?.removeEventListener('transitionend', schedule);
  };
}
